import { useQuery } from "@tanstack/react-query";
import { useAppAuth } from "@/lib/useAppAuth";
import { retryUnlessRateLimited, backoffRetryDelay } from "@/lib/queryClient";

export type AuthMe = {
  userId: string;
  isAdmin: boolean;
  // Paid-access gate inputs. `billingConfigured` is false when Stripe isn't
  // connected (gate dormant → everyone reads active). `subscriptionActive` is
  // the cheap mirror read; admin + gate-dormant both report active.
  subscriptionActive?: boolean;
  billingConfigured?: boolean;
  // Best available human name (Clerk → Stripe → owner name → email local part),
  // resolved server-side. Drives the profile avatar initials. Null when unknown.
  displayName?: string | null;
  // The owner's primary account email (resolved server-side from Clerk). Used as
  // the default/placeholder for notification-recipient fields. Null when unknown.
  accountEmail?: string | null;
  // Authorized users (Task 1945): set when the signed-in user is a member
  // working inside an OWNER's workspace. Drives the sidebar "Working in
  // <owner>'s account" indicator and hides owner-only Settings sections.
  actingFor?: { ownerName: string | null; ownerEmail: string | null } | null;
  // Admin "View as user" support session (Task 2057): set ONLY when the real
  // signed-in user is the admin working inside a subscriber's account. Drives
  // the persistent banner + Exit button in AppShell.
  supportSession?: { targetUserId: string; targetName: string | null; targetEmail: string | null } | null;
};

// Identity/admin hook. `isAdmin` controls access to the admin page only.
// `subscriptionActive`/`billingConfigured` drive the paid-access gate. Fetches
// /api/auth/me with the Clerk session token so the server can resolve the
// user's id, admin status, and billing state. Disabled until signed in.
export function useAccessTier() {
  const { isSignedIn, getToken } = useAppAuth();
  const query = useQuery<AuthMe>({
    queryKey: ["/api/auth/me"],
    enabled: isSignedIn === true,
    queryFn: async () => {
      const token = await getToken();
      const res = await fetch("/api/auth/me", {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const err = new Error(`Auth check failed: ${res.status}`) as Error & {
          status?: number;
          retryAfterSeconds?: number;
        };
        err.status = res.status;
        // Honor Retry-After on 429 so the rate-limited fallback can wait
        // exactly as long as the server asks (draft-7 standardHeaders emit it
        // in seconds). Ignore date-form / unparseable values.
        const retryAfter = Number(res.headers.get("Retry-After"));
        if (res.status === 429 && Number.isFinite(retryAfter) && retryAfter > 0) {
          err.retryAfterSeconds = retryAfter;
        }
        throw err;
      }
      return res.json();
    },
    staleTime: Infinity,
    retry: retryUnlessRateLimited(1),
    retryDelay: backoffRetryDelay,
  });

  // The per-IP limiter returns 429 while the window is tripped. Surface that
  // distinctly so the app can show a friendly "slow down" state (with an
  // automatic retry) instead of an indefinite spinner. The queryFn stamps a
  // numeric `status` on the error; fall back to parsing the message.
  const errorStatus =
    (query.error as { status?: number } | null)?.status ??
    (query.error instanceof Error
      ? Number(query.error.message.match(/\b(\d{3})\b/)?.[1])
      : undefined);

  return {
    isLoading: query.isLoading,
    isRateLimited: query.isError && errorStatus === 429,
    // Server-provided Retry-After (seconds) from the last 429, if any. The
    // rate-limited fallback uses it to time its automatic retry.
    rateLimitRetryAfterSeconds:
      (query.error as { retryAfterSeconds?: number } | null)
        ?.retryAfterSeconds,
    refetch: query.refetch,
    isAdmin: query.data?.isAdmin ?? false,
    // Default to active so the gate fails OPEN while loading or if the field is
    // absent (e.g. older server) — the server is the authoritative gate.
    subscriptionActive: query.data?.subscriptionActive ?? true,
    billingConfigured: query.data?.billingConfigured ?? false,
    authMe: query.data,
  };
}
